import { forwardRef, useEffect, useState } from 'react'
import { Clock, Star } from 'lucide-react'
import { Button, Separator, ThemeSwitcher } from '@/components/ui'
import { RestaurantInfoDialog } from '@/components/order'
import { useMenuData } from '@/hooks/useMenuData'
import { useWaitTime } from '@/hooks/useWaitTime'
import { useSelectedLocation } from '@/hooks/useSelectedLocation'

type RestaurantHeaderProps = {
  onHeightChange?: (height: number) => void
}

export const RestaurantHeader = forwardRef<HTMLDivElement, RestaurantHeaderProps>(
  function RestaurantHeaderInner({ onHeightChange }, ref) {
    const [isInfoDialogOpen, setIsInfoDialogOpen] = useState(false)

    const { restaurant, isLoading } = useMenuData()
    const { waitTime } = useWaitTime()
    const { address: locationAddress } = useSelectedLocation()

    // Notify parent of height changes
    useEffect(() => {
      if (!ref || typeof ref === 'function' || !ref.current) return
      const el: HTMLDivElement | null = ref.current
      if (!el) return
      const report = () => onHeightChange?.(el.offsetHeight)
      report()
      const ro = new ResizeObserver(() => report())
      ro.observe(el)
      return () => ro.disconnect()
    }, [ref, onHeightChange])

    // Image loading states
    const [heroLoaded, setHeroLoaded] = useState(false)
    const [logoLoaded, setLogoLoaded] = useState(false)

    const heroImage = restaurant?.banner_image_url
    const logoImage = restaurant?.logo_url

    const showHeroSkeleton = isLoading || (heroImage && !heroLoaded)
    const showLogoSkeleton = isLoading || (logoImage && !logoLoaded)

    // Restaurant metadata
    const rating = restaurant?.rating
    const reviewCount = restaurant?.review_count
    const cuisineTags = restaurant?.cuisine_tags || []
    const cuisineDisplay = cuisineTags.length > 0 ? cuisineTags.join(' \u2022 ') : null

    return (
      <div ref={ref} className="mb-4 sm:mb-6">
        {/* Hero banner with logo */}
        <div className="relative w-full">
          <div className="relative w-full aspect-[16/6] sm:aspect-[16/5] md:aspect-[16/4] rounded-2xl sm:rounded-3xl overflow-hidden shadow-lg bg-muted">
            {showHeroSkeleton && (
              <div className="absolute inset-0 bg-gradient-to-r from-muted via-muted-foreground/10 to-muted animate-shimmer bg-[length:200%_100%]" />
            )}
            {heroImage && (
              <img
                src={heroImage}
                alt={restaurant?.title || 'Restaurant banner'}
                loading="eager"
                fetchPriority="high"
                decoding="async"
                onLoad={() => setHeroLoaded(true)}
                className={`w-full h-full object-cover transition-opacity duration-300 ${
                  heroLoaded ? 'opacity-100' : 'opacity-0'
                }`}
              />
            )}
            {heroLoaded && heroImage && (
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
            )}
          </div>

          {/* Logo overlay */}
          <div className="absolute bottom-0 left-4 sm:left-6 translate-y-1/2">
            <div className="w-16 h-16 sm:w-[72px] sm:h-[72px] md:w-20 md:h-20 rounded-full bg-background shadow-lg flex items-center justify-center overflow-hidden border-4 border-background">
              {showLogoSkeleton && (
                <div className="absolute inset-0 bg-gradient-to-r from-muted via-muted-foreground/10 to-muted animate-shimmer bg-[length:200%_100%] rounded-full" />
              )}
              {logoImage && (
                <img
                  src={logoImage}
                  alt="Restaurant logo"
                  onLoad={() => setLogoLoaded(true)}
                  className={`w-full h-full object-cover transition-opacity duration-300 ${
                    logoLoaded ? 'opacity-100' : 'opacity-0'
                  }`}
                />
              )}
            </div>
          </div>
        </div>

        {/* Restaurant Info */}
        <div className="mt-10 sm:mt-11 md:mt-12 flex flex-col gap-4">
          {/* Mobile Layout */}
          <div className="md:hidden">
            <div>
              <div className="flex items-center justify-between">
                <h1 className="text-2xl font-bold">
                  {restaurant?.title || 'Restaurant'}
                </h1>
                <ThemeSwitcher />
              </div>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                {waitTime && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-muted text-sm font-medium">
                    <Clock className="h-3.5 w-3.5 shrink-0" />
                    ~{waitTime.estimated_wait_minutes} min
                  </span>
                )}
                {rating && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-muted text-sm font-medium">
                    <Star className="h-3.5 w-3.5 fill-yellow-400 text-yellow-400" />
                    {rating}
                    {reviewCount && <span className="text-muted-foreground font-normal">({reviewCount})</span>}
                  </span>
                )}
                {cuisineDisplay && (
                  <span className="text-sm text-muted-foreground">{cuisineDisplay}</span>
                )}
                <Button
                  variant="link"
                  className="h-auto p-0 text-sm text-foreground underline"
                  onClick={() => setIsInfoDialogOpen(true)}
                >
                  Info
                </Button>
              </div>
              <div className="text-sm text-muted-foreground mt-1.5">
                <span>{locationAddress}</span>
              </div>
            </div>
          </div>

          {/* Desktop Layout */}
          <div className="hidden md:block">
            <div className="flex flex-row items-start justify-between gap-4">
              <div className="flex-1">
                <h1 className="text-3xl md:text-4xl font-bold mb-2">
                  {restaurant?.title || 'Restaurant'}
                </h1>
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  {waitTime && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-muted text-sm font-medium">
                      <Clock className="h-3.5 w-3.5 shrink-0" />
                      ~{waitTime.estimated_wait_minutes} min
                    </span>
                  )}
                  {rating && (
                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-muted text-sm font-medium">
                      <Star className="h-3.5 w-3.5 fill-yellow-400 text-yellow-400" />
                      {rating}
                      {reviewCount && <span className="text-muted-foreground font-normal">({reviewCount})</span>}
                    </span>
                  )}
                  {cuisineDisplay && (
                    <span className="text-sm text-muted-foreground">{cuisineDisplay}</span>
                  )}
                  <Button
                    variant="link"
                    className="h-auto p-0 text-sm text-foreground underline"
                    onClick={() => setIsInfoDialogOpen(true)}
                  >
                    Info
                  </Button>
                </div>
                <div className="text-sm text-muted-foreground">
                  <span>{locationAddress}</span>
                </div>
              </div>

              <ThemeSwitcher />
            </div>
          </div>

          <Separator />
        </div>

        {/* Restaurant Info Dialog */}
        <RestaurantInfoDialog
          isOpen={isInfoDialogOpen}
          onClose={() => setIsInfoDialogOpen(false)}
          restaurant={restaurant}
        />
      </div>
    )
  }
)
